import {
  MODEL_IDS,
  RETIRED_MODEL_SUCCESSORS,
  type LLMSelection,
} from "@shaarp/common";
import { useSyncExternalStore } from "react";

const STORAGE_KEY = "llm-selection-v3";
const DEFAULT_SELECTION: LLMSelection = "auto";

/**
 * The selection as the browser last stored it.
 *
 * Reading is wrapped because storage is not always readable: Safari in private
 * browsing and a profile with cookies blocked both throw on access, and a
 * throw here would take the composer's whole render with it.
 */
export function readStoredLlmSelection(): LLMSelection {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_SELECTION;
    // Written as JSON, but a value left by an older build is a bare string.
    let parsed: string;
    try {
      parsed = JSON.parse(stored) as string;
    } catch {
      parsed = stored;
    }

    return resolveSelection(parsed);
  } catch {
    return DEFAULT_SELECTION;
  }
}

/**
 * The stored value as today's catalogue understands it.
 *
 * A model that moved generation resolves to its successor, so the user stays
 * with the provider they picked. Anything else the catalogue no longer offers
 * goes back to `auto`, which the API resolves to the team default.
 */
function resolveSelection(stored: string): LLMSelection {
  const successor =
    RETIRED_MODEL_SUCCESSORS[stored as keyof typeof RETIRED_MODEL_SUCCESSORS];
  const candidate = successor ?? stored;

  if (candidate === DEFAULT_SELECTION) return DEFAULT_SELECTION;

  return (MODEL_IDS as readonly string[]).includes(candidate)
    ? (candidate as LLMSelection)
    : DEFAULT_SELECTION;
}

/**
 * The selection itself, held outside React.
 *
 * Module level, because that is the whole point: the readers are mounted under
 * different subtrees — the composer, its mobile twin, the conversation view,
 * the company panel — and a copy per reader is what let two views disagree
 * about which model the next message goes to. One value, one set of listeners,
 * and `useSyncExternalStore` to read it: the same shape as
 * `company-preview-panel-store`, and the reason none of this needs an effect to
 * resynchronise a reader that mounted late.
 *
 * Read lazily rather than at import: storage access can throw, and doing it
 * here would run before the first render of anything.
 */
const llmSelectionStore = {
  selection: null as LLMSelection | null,
  listeners: new Set<() => void>(),
  getSnapshot(): LLMSelection {
    llmSelectionStore.selection ??= readStoredLlmSelection();
    return llmSelectionStore.selection;
  },
  subscribe(listener: () => void): () => void {
    llmSelectionStore.listeners.add(listener);
    return () => {
      llmSelectionStore.listeners.delete(listener);
    };
  },
  set(selection: LLMSelection): void {
    if (llmSelectionStore.selection === selection) return;
    llmSelectionStore.selection = selection;
    llmSelectionStore.listeners.forEach((notify) => notify());
  },
};

/**
 * Stores the selection and tells every reader.
 *
 * Written outside the hook so the identity is stable: the setter is a
 * dependency of half the composer's callbacks.
 */
function setLlmSelection(selection: LLMSelection): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // A selection that cannot be stored still holds for this session: the
    // store below is what the app actually reads.
  }
  llmSelectionStore.set(selection);
}

// Another tab of the same profile writing the key: the store is per document,
// so without this the two tabs drift until one of them is reloaded.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event: StorageEvent) => {
    // `null` is the whole store being cleared, which is also a change to it.
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    llmSelectionStore.set(readStoredLlmSelection());
  });
}

/**
 * The chat model the user picked, and the only place it is read or written.
 *
 * Kept in the browser rather than on the user — unlike the clarifying-questions
 * switch — because it is a per-device habit, but shared the same way: one
 * source of truth every reader subscribes to. Held in a `useState` per caller,
 * it was frozen at mount for every view keyed on the conversation, so changing
 * model mid-thread and answering from anywhere but the composer sent the model
 * that was selected when the thread was opened.
 *
 * @returns The current selection, and a setter every reader hears.
 */
export function useLlmSelection(): {
  readonly llmSelection: LLMSelection;
  readonly setLlmSelection: (selection: LLMSelection) => void;
} {
  const llmSelection = useSyncExternalStore(
    llmSelectionStore.subscribe,
    llmSelectionStore.getSnapshot
  );

  return { llmSelection, setLlmSelection };
}
